import { useMediaQuery, useTheme } from '@mui/material';
import React from 'react';

import { RouteContext } from 'src/context';
import { SettingsPageColumn } from 'src/features/settings/layout';
import { useCurrentSettingsPage } from 'src/features/settings/registry/useCurrentSettingsPage';
import { useSettingsVisibilityContext } from 'src/features/settings/registry/useSettingsVisibilityContext';
import {
  SettingsSaveBarProvider,
  SettingsSaveBarSlot,
} from 'src/features/settings/save-bar';
import { getSettingsSidebarPlanBadge } from 'src/features/settings/sidebar/settingsSidebarSections';
import { SettingsPlanTag } from 'src/features/settings/SettingsPlanTag';

import { useHeaderSlot } from '../../AppShell/HeaderSlotContext';
import { useShell } from '../../AppShell/ShellContext';

export interface SettingsColumnVariantProps {
  children: React.ReactNode;
}

/**
 * The `new-settings` page layout: the 760px column under the title and
 * description from the page's registry entry. Sub-pages have no entry and keep
 * their route's title, as SettingsPageToolbar does.
 */
export const SettingsColumnVariant: React.FC<SettingsColumnVariantProps> = ({
  children,
}) => {
  const page = useCurrentSettingsPage();
  const visibilityContext = useSettingsVisibilityContext();
  // The title carries the same plan tag as the page's sidebar row, so a locked
  // page names the plan it needs.
  const planBadge = page
    ? getSettingsSidebarPlanBadge(page, visibilityContext)
    : null;
  const { title: routeTitle, description: routeDescription } =
    React.useContext(RouteContext);
  const { drawerWidth, isResizableSidebarActive } = useShell();
  const { showSidebar } = useHeaderSlot();
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const isSmUp = useMediaQuery(theme.breakpoints.up('sm'));

  // The settings sidebar is fixed-position on desktop, so the content clears it.
  const paddingLeft =
    isMdUp && showSidebar && !isResizableSidebarActive ? drawerWidth : 0;

  return (
    <div
      // AppShell stops scrolling on phones, so the page scrolls itself there.
      // Relative so absolutely placed pieces (AG Grid's hidden aria node) stay
      // inside the scrolling area instead of stretching the document and adding
      // a second, page-level scrollbar.
      className={`relative flex w-full flex-col ${isSmUp ? '' : 'overflow-y-auto'}`}
      style={{ paddingLeft }}
    >
      <SettingsSaveBarProvider>
        <SettingsSaveBarSlot />
        <SettingsPageColumn
          title={page?.label ?? routeTitle ?? ''}
          description={page?.description ?? routeDescription}
          docsUrl={page?.docsUrl}
          titleBadge={
            planBadge?.kind === 'planTag' ? (
              <SettingsPlanTag label={planBadge.label} size="md" />
            ) : null
          }
        >
          {children}
        </SettingsPageColumn>
      </SettingsSaveBarProvider>
    </div>
  );
};
